import { courseDisplayName, formatDate, formatTime } from '@/lib/utils';

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const ATTENDANCE_COLUMNS = ['1', '2', '3', '4'];

export const LIST_KINDS = [
  { id: 'teilnehmer', label: 'Teilnehmerliste' },
  { id: 'anwesenheit', label: 'Anwesenheitsliste' },
];

export const LIST_FIELDS = [
  { id: 'name', label: 'Name Teilnehmer:in', header: 'Name' },
  { id: 'fach', label: 'Unterrichtsfach / Bezeichnung', header: 'Fach / Bezeichnung' },
  { id: 'lehrkraft', label: 'Lehrkraft', header: 'Lehrkraft' },
  { id: 'tag', label: 'Unterrichtstag', header: 'Tag' },
  { id: 'zeit', label: 'Unterrichtszeit', header: 'Zeit' },
  { id: 'ort', label: 'Unterrichtsort', header: 'Ort' },
  { id: 'raum', label: 'Raum', header: 'Raum' },
  { id: 'geburt', label: 'Geburtsdatum', header: 'Geburtsdatum' },
  { id: 'adresse', label: 'Adresse', header: 'Adresse' },
  { id: 'telefon', label: 'Telefonnummer', header: 'Telefon' },
  { id: 'email', label: 'E-Mail-Adresse', header: 'E-Mail' },
];

const DEFAULT_FIELDS = {
  teilnehmer: ['name', 'fach', 'lehrkraft', 'tag', 'zeit', 'ort', 'raum'],
  anwesenheit: ['name', 'fach', 'tag', 'zeit'],
};

export function defaultFields(kind) {
  return [...(DEFAULT_FIELDS[kind] || DEFAULT_FIELDS.teilnehmer)];
}

const SELECTION_KEY = 'uv-listen-zusammenstellung';
const KNOWN_FIELDS = new Set(LIST_FIELDS.map((field) => field.id));

function sanitizeFields(ids, kind) {
  const next = (Array.isArray(ids) ? ids : []).filter((id) => KNOWN_FIELDS.has(id));
  return next.length ? next : defaultFields(kind);
}

export function readListSelection() {
  try {
    const raw = JSON.parse(localStorage.getItem(SELECTION_KEY) || 'null');
    if (!raw || typeof raw !== 'object') return null;
    const kind = LIST_KINDS.some((item) => item.id === raw.kind) ? raw.kind : 'teilnehmer';
    const byKind = raw.fieldsByKind || {};
    return {
      kind,
      fieldsByKind: {
        teilnehmer: sanitizeFields(byKind.teilnehmer, 'teilnehmer'),
        anwesenheit: sanitizeFields(byKind.anwesenheit, 'anwesenheit'),
      },
    };
  } catch {
    return null;
  }
}

export function writeListSelection(selection) {
  localStorage.setItem(SELECTION_KEY, JSON.stringify(selection));
}

const TEMPLATE_KEY = 'uv-listen-vorlagen';

export const STARTER_TEMPLATES = [
  { id: 'vorspiel', name: 'Vorspiel', kind: 'teilnehmer', fields: ['name', 'zeit', 'ort'] },
  { id: 'anwesenheit', name: 'Anwesenheitsliste', kind: 'anwesenheit', fields: ['name', 'fach', 'tag', 'zeit'] },
  {
    id: 'teilnehmer-kontakte',
    name: 'Teilnehmerliste mit Kontaktdaten',
    kind: 'teilnehmer',
    fields: ['name', 'fach', 'geburt', 'adresse', 'telefon', 'email'],
  },
];

export function templateTestId(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function sanitizeTemplate(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = String(raw.name || '').trim().slice(0, 80);
  const kind = LIST_KINDS.some((item) => item.id === raw.kind) ? raw.kind : null;
  if (!name || !kind) return null;
  const id = templateTestId(raw.id || name);
  if (!id) return null;
  return { id, name, kind, fields: sanitizeFields(raw.fields, kind) };
}

function readCustomTemplates() {
  try {
    const raw = JSON.parse(localStorage.getItem(TEMPLATE_KEY) || '[]');
    return (Array.isArray(raw) ? raw : []).map(sanitizeTemplate).filter(Boolean);
  } catch {
    return [];
  }
}

export function readListTemplates() {
  const byId = new Map(STARTER_TEMPLATES.map((item) => [item.id, { ...item, fields: [...item.fields] }]));
  readCustomTemplates().forEach((item) => byId.set(item.id, item));
  return [...byId.values()];
}

export function saveListTemplate({ name, kind, fields }) {
  const next = sanitizeTemplate({ name, kind, fields });
  if (!next) return readListTemplates();
  const custom = readCustomTemplates().filter((item) => item.id !== next.id);
  localStorage.setItem(TEMPLATE_KEY, JSON.stringify([...custom, next]));
  return readListTemplates();
}

function personName(student) {
  const name = [student?.last_name, student?.first_name].filter(Boolean).join(', ');
  return name || '—';
}

function textOrDash(value) {
  const text = String(value ?? '').trim();
  return text || '—';
}

function addressLine(student) {
  const place = [student?.postal_code, student?.city].map((part) => String(part ?? '').trim()).filter(Boolean).join(' ');
  const line = [String(student?.address ?? '').trim(), place].filter(Boolean).join(', ');
  return line || '—';
}

function cellsFor(enrollment) {
  const student = enrollment?.student || {};
  const course = enrollment?.course || {};
  return {
    name: personName(student),
    fach: courseDisplayName(course.name, course.subject_name) || '—',
    lehrkraft: textOrDash(course.teacher_name),
    tag: course.day_of_week != null && DAYS[course.day_of_week] ? DAYS[course.day_of_week] : '—',
    zeit: formatTime(course.start_time) || '—',
    ort: textOrDash(course.location_name),
    raum: textOrDash(course.room_name),
    geburt: formatDate(student.date_of_birth) || '—',
    adresse: addressLine(student),
    telefon: textOrDash(student.phone),
    email: textOrDash(student.email),
  };
}

/** Tabelle für die aktuell geladenen Übersicht-Einträge. fieldIds bestimmt die Spalten. */
export function listTable(enrollments, kind, fieldIds = defaultFields(kind)) {
  const chosen = new Set(fieldIds);
  const fields = LIST_FIELDS.filter((field) => chosen.has(field.id));
  const attendance = kind === 'anwesenheit';
  const headers = [
    ...fields.map((field) => field.header),
    ...(attendance ? ATTENDANCE_COLUMNS : []),
  ];
  const rows = (enrollments || []).map((enrollment) => {
    const cell = cellsFor(enrollment);
    const values = fields.map((field) => cell[field.id]);
    if (attendance) values.push('', '', '', '');
    return values;
  });
  return {
    title: attendance ? 'Anwesenheitsliste' : 'Teilnehmerliste',
    headers,
    rows,
  };
}
